import {
  type GetEnterpriseServiceOptionListQuery,
  type Instacart_Enterprise_Frigate_Fulfillment_V1_Enterprise_Service_Options_EnterpriseServiceOption,
  useGetEnterpriseServiceOptionListQuery,
} from '../../../../api'
import { type EagerResponse, useApolloRequestWrapper } from '../../../api'
import { assertExhaustive } from '../../../utils/assertExhaustive'
import {
  type GetEnterpriseServiceOptionListParams,
  type ServiceOptionList,
  type ServiceOption,
  type ServiceOptionWindow,
} from './types'

function mapWindow(
  serviceOption: Instacart_Enterprise_Frigate_Fulfillment_V1_Enterprise_Service_Options_EnterpriseServiceOption
): ServiceOptionWindow {
  if (serviceOption.immediateWindow) {
    return {
      type: 'immediate',
      startsAt: serviceOption.immediateWindow.startsAt,
      endsAt: serviceOption.immediateWindow.endsAt,
      immediateHour: serviceOption.immediateWindow.immediateHour!,
    }
  }
  if (serviceOption.scheduledWindow) {
    return {
      type: 'scheduled',
      startsAt: serviceOption.scheduledWindow.startsAt,
      endsAt: serviceOption.scheduledWindow.endsAt,
      asap: serviceOption.scheduledWindow.asap!,
    }
  }
  if (serviceOption.asapWindow) {
    return {
      type: 'asap',
      startsAt: serviceOption.asapWindow.startsAt,
      endsAt: serviceOption.asapWindow.endsAt,
      asap: serviceOption.asapWindow.asap!,
    }
  }
  if (serviceOption.standardEtaWindow) {
    return {
      type: 'standard_eta',
      startsAt: serviceOption.standardEtaWindow.startsAt,
      endsAt: serviceOption.standardEtaWindow.endsAt,
      asap: serviceOption.standardEtaWindow.asap!,
    }
  }
  if (serviceOption.priorityEtaWindow) {
    return {
      type: 'priority_eta',
      startsAt: serviceOption.priorityEtaWindow.startsAt,
      endsAt: serviceOption.priorityEtaWindow.endsAt,
      asap: serviceOption.priorityEtaWindow.asap!,
    }
  }
  // unreachable
  // @ts-expect-error
  assertExhaustive(serviceOption)
}

function mapResponse(data: GetEnterpriseServiceOptionListQuery): ServiceOptionList {
  const response =
    data.instacart_enterprise_frigate_fulfillment_v1_EnterpriseServiceOptionService_GetEnterpriseServiceOptionList

  if (!response?.results) {
    return {
      results: [],
      zoneId: null,
      warnings: [],
      flags: {
        isLongDistanceDelivery: false,
      },
    }
  }

  return {
    results: response.results.map(
      (serviceOption): ServiceOption => ({
        id: serviceOption.id!,
        serviceOptionReference: serviceOption.serviceOptionReference!,
        type: serviceOption.type!,
        availability: {
          isAvailable: serviceOption.availability?.isAvailable ?? false,
          unavailabilityReasons: serviceOption.availability?.unavailabilityReasons ?? [],
          itemCodes: serviceOption.availability?.itemCodes ?? [],
        },
        date: serviceOption.date!,
        window: mapWindow(serviceOption),
      })
    ),
    zoneId: response.zoneId ?? null,
    warnings: response.warnings ?? [],
    flags: {
      isLongDistanceDelivery: response.flags?.isLongDistanceDelivery ?? false,
    },
  }
}

export const useGetEnterpriseServiceOptionList = (
  params: GetEnterpriseServiceOptionListParams,
  { skip = false }: { skip?: boolean } = {}
): EagerResponse<ServiceOptionList> => {
  const query = useGetEnterpriseServiceOptionListQuery({
    variables: params,
    fetchPolicy: 'cache-and-network',
    refetchWritePolicy: 'overwrite',
    skip,
  })
  return useApolloRequestWrapper({ query, mapResponse })
}
